import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  ReflectiveInjector,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';

import {
  animate,
  AnimationEvent,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';

import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { SkyFlyoutAdapterService } from './flyout-adapter.service';
import { SkyFlyoutInstance } from './flyout-instance';

import {
  SkyFlyoutConfig,
  SkyFlyoutMessage,
  SkyFlyoutMessageType
} from './types';

const FLYOUT_OPEN_STATE = 'flyoutOpen';
const FLYOUT_CLOSED_STATE = 'flyoutClosed';


export class SkyFlyoutComponent implements OnDestroy, OnInit {
  public config: SkyFlyoutConfig;
  public flyoutState = FLYOUT_CLOSED_STATE;
  public isOpen = false;
  public isOpening = false;

  public flyoutWidth = 0;
  public isDragging = false;
  private xCoord = 0;

  public get messageStream(): Subject<SkyFlyoutMessage> {
    return this._messageStream;
  }

  
  private target: ViewContainerRef;

  
  private flyoutHeader: ElementRef;

  private flyoutInstance: SkyFlyoutInstance<any>;
  private ngUnsubscribe = new Subject();

  private _messageStream = new Subject<SkyFlyoutMessage>();

  constructor(
    private adapter: SkyFlyoutAdapterService,
    private changeDetector: ChangeDetectorRef,
    private injector: Injector,
    private resolver: ComponentFactoryResolver
  ) {
    // All commands flow through the message stream.
    this.messageStream
      .takeUntil(this.ngUnsubscribe)
      .subscribe((message: SkyFlyoutMessage) => {
        this.handleIncomingMessages(message);
      });
  }

  public ngOnInit() {
    this.adapter.adjustHeaderForHelp(this.flyoutHeader);
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onCloseButtonClick() {
    this.messageStream.next({
      type: SkyFlyoutMessageType.Close
    });
  }

  public attach<T>(component: Type<T>, config: SkyFlyoutConfig): SkyFlyoutInstance<T> {
    this.cleanTemplate();

    // Emit the closed event on any previously opened flyout instance
    if (this.flyoutInstance) {
      this.notifyClosed();
    }

    this.config = Object.assign({ providers: [] }, config);
    this.config.defaultWidth = this.config.defaultWidth || 500;
    this.config.minWidth = this.config.minWidth || 320;
    this.config.maxWidth = this.config.maxWidth || this.config.defaultWidth;

    const factory = this.resolver.resolveComponentFactory(component);
    const providers = ReflectiveInjector.resolve(this.config.providers);
    const injector = ReflectiveInjector.fromResolvedProviders(providers, this.injector);
    const componentRef = this.target.createComponent(factory, undefined, injector);

    this.flyoutInstance = this.createFlyoutInstance<T>(componentRef.instance);

    // Open the flyout immediately.
    this.messageStream.next({
      type: SkyFlyoutMessageType.Open
    });

    this.flyoutWidth = this.config.defaultWidth;

    return this.flyoutInstance;
  }

  public getAnimationState(): string {
    return (this.isOpening) ? FLYOUT_OPEN_STATE : FLYOUT_CLOSED_STATE;
  }

  public animationDone(event: AnimationEvent) {
    if (event.toState === FLYOUT_OPEN_STATE) {
      this.isOpen = true;
    }

    if (event.toState === FLYOUT_CLOSED_STATE) {
      this.isOpen = false;
      this.notifyClosed();
      this.cleanTemplate();
    }
  }

  public onMouseDown(event: MouseEvent) {
    this.isDragging = true;
    this.xCoord = event.clientX;
    event.preventDefault();
    event.stopPropagation();
  }

  
  public onMouseMove(event: MouseEvent) {
    if (!this.isDragging) {
      return;
    }

    const offsetX = event.clientX - this.xCoord;
    let width = this.flyoutWidth;

    width -= offsetX;

    if (width < this.config.minWidth || width > this.config.maxWidth) {
      return;
    }

    this.flyoutWidth = width;
    this.xCoord = event.clientX;
  }

  
  public onHandleRelease(event: MouseEvent) {
    this.isDragging = false;
  }

  private open() {
    if (!this.isOpen) {
      this.isOpen = false;
      this.isOpening = true;
    }

    this.changeDetector.markForCheck();
  }

  private close() {
    this.isOpen = true;
    this.isOpening = false;
    this.changeDetector.markForCheck();
  }

  private createFlyoutInstance<T>(component: T): SkyFlyoutInstance<T> {
    const instance = new SkyFlyoutInstance<T>();

    instance.componentInstance = component;
    instance.hostController
      .takeUntil(this.ngUnsubscribe)
      .subscribe((message: SkyFlyoutMessage) => {
        this.messageStream.next(message);
      });

    return instance;
  }

  private handleIncomingMessages(message: SkyFlyoutMessage) {
    /* tslint:disable-next-line:switch-default */
    switch (message.type) {
      case SkyFlyoutMessageType.Open:
      this.open();
      break;

      case SkyFlyoutMessageType.Close:
      this.close();
      break;
    }
  }

  private notifyClosed() {
    this.flyoutInstance.closed.emit();
    this.flyoutInstance.closed.complete();
  }

  private cleanTemplate() {
    this.target.clear();
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-flyout',
  template: `<div
  class="sky-flyout sky-shadow"
  tabindex="-1"
  [attr.role]="config?.ariaRole"
  [attr.aria-describedby]="config?.ariaDescribedBy"
  [attr.aria-labelledby]="config?.ariaLabelledBy"
  [ngClass]="{ 'sky-flyout-hidden': !isOpen && !isOpening }"
  (@flyoutState.done)="animationDone($event)"
  [@flyoutState]="getAnimationState()"
  [style.width.px]="flyoutWidth">

  <button
    type="button"
    class="sky-flyout-resize-handle"
    aria-hidden="true"
    role="separator"
    tabindex="-1"
    (mousedown)="onMouseDown($event)">
  </button>

  <div class="sky-flyout-header" #flyoutHeader>
    <div class="sky-flyout-header-content"></div>
    <div class="sky-flyout-header-buttons">
      <button
        type="button"
        class="sky-btn sky-btn-default sky-flyout-btn-close"
        (click)="onCloseButtonClick()"
        [attr.aria-label]="'flyout_close' | skyResources">
        <i class="fa fa-close" aria-hidden="true"></i>
      </button>
    </div>
  </div>
  <div class="sky-flyout-content">
    <div #target></div>
  </div>
</div>
`,
  styles: [`.sky-flyout {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  background-color: #fff;
  border-left: 6px solid #00b4f1;
  z-index: 1001;
}

.sky-flyout:focus {
  outline: none;
}

@media (max-width: 768px) {
  .sky-flyout {
    min-width: 100%;
    max-width: 100%;
  }
}

.sky-flyout.sky-flyout-hidden {
  visibility: hidden;
}

.sky-flyout-resize-handle {
  height: 100%;
  width: 14px;
  position: absolute;
  left: -10px;
  cursor: ew-resize;
  padding: 0;
  border: 0;
  background: transparent;
  display: block;
  top: 0;
  bottom: 0;
}

@media (max-width: 768px) {
  .sky-flyout-resize-handle {
    cursor: initial;
  }
}

.sky-flyout-header {
  border-bottom: 1px solid #cdcfd2;
  width: 100%;
  background: #eeeeef;
  height: 50px;
  padding: 8px 15px;
  display: flex;
  align-items: baseline;
}

.sky-flyout-header-content {
  flex-grow: 1;
}

.sky-flyout-help-shim {
  padding-right: 8px;
}

@media (min-width: 768px) {
  .sky-flyout-help-shim {
    padding-right: 50px;
  }
}

.sky-flyout-content {
  overflow-y: auto;
  height: calc(100% - 50px);
}
`],
  animations: [
    trigger('flyoutState', [
      state(FLYOUT_OPEN_STATE, style({ transform: 'translateX(0)' })),
      state(FLYOUT_CLOSED_STATE, style({ transform: 'translateX(100%)' })),
      transition('void => *', [
        style({ transform: 'translateX(100%)' }),
        animate(250)
      ]),
      transition(`* <=> *`, animate('250ms ease-in'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: SkyFlyoutAdapterService, },
{type: ChangeDetectorRef, },
{type: Injector, },
{type: ComponentFactoryResolver, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'target': [{ type: ViewChild, args: ['target', { read: ViewContainerRef }, ] },],
'flyoutHeader': [{ type: ViewChild, args: ['flyoutHeader', ] },],
'onMouseMove': [{ type: HostListener, args: ['document:mousemove', ['$event'], ] },],
'onHandleRelease': [{ type: HostListener, args: ['document:mouseup', ['$event'], ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
